<template>
  <div class="ridership-time-range-filter d-flex align-items-center gap-2">
    <label class="small text-muted mb-0">{{ $t('flexigo.ridership.fromTime') }}</label>
    <time-picker
      :value="displayFromTime"
      :format="timeFormat"
      :minute-interval="5"
      manual-input
      close-on-complete
      @change="onFromChange"
    />
    <label class="small text-muted mb-0">{{ $t('flexigo.ridership.toTime') }}</label>
    <time-picker
      :value="displayToTime"
      :format="timeFormat"
      :minute-interval="5"
      manual-input
      close-on-complete
      @change="onToChange"
    />
  </div>
</template>

<script>
import timePicker from 'vue2-timepicker';
import { localFormatTimeString } from '@/helpers/dateTime';
import { isLocaleEnUs } from '@/helpers/utility';

export default {
  name: 'RidershipTimeRangeFilter',
  components: { timePicker },
  props: {
    fromTime: {
      type: String,
      default: null,
    },
    toTime: {
      type: String,
      default: null,
    },
  },
  computed: {
    /**
     * Display format for both fields, keyed on the browser locale — the same signal the
     * charts format their clock labels with, so the range here and the range quoted in a
     * tooltip read alike (VSER-14676). A native `<input type="time">` cannot do that: it
     * renders in the browser's UI locale, which need not be the one the app formats by.
     *
     * 'h:mm A' rather than 'hh:mm A' because that is exactly what `localFormatTimeString`
     * produces, and the picker parses the value it is handed with this same format.
     */
    timeFormat() {
      return isLocaleEnUs() ? 'h:mm A' : 'HH:mm';
    },
    displayFromTime() {
      return localFormatTimeString(this.fromTime);
    },
    displayToTime() {
      return localFormatTimeString(this.toTime);
    },
  },
  methods: {
    /**
     * The model stays 24-hour "HH:mm" whatever the field displays: the picker reports every
     * token it knows for the picked time, so the 24-hour pair is read straight off it.
     * An empty field (cleared) becomes null, which the dashboard reads as "no time filter".
     */
    toModelTime({ data } = {}) {
      const { HH, mm } = data || {};
      return HH && mm ? `${HH}:${mm}` : null;
    },
    onFromChange(payload) {
      const fromTime = this.toModelTime(payload);
      let { toTime } = this;
      if (fromTime && toTime && toTime < fromTime) {
        toTime = fromTime;
      }
      this.$emit('change', { fromTime, toTime });
    },
    onToChange(payload) {
      const toTime = this.toModelTime(payload);
      let { fromTime } = this;
      if (fromTime && toTime && toTime < fromTime) {
        fromTime = toTime;
      }
      this.$emit('change', { fromTime, toTime });
    },
  },
};
</script>

<style scoped>
/* Sizing only — the picker's colors come from the module's own skin of it, in
   sass/flexigo/_base.scss. Its CSS is em-based, so the font size scales the field and its
   dropdown together, and the rules have to reach into the child to land on them. */
.ridership-time-range-filter ::v-deep .vue__time-picker {
  font-size: 12px;
}

.ridership-time-range-filter ::v-deep .vue__time-picker input.display-time {
  height: auto;
  padding: 0.35em 0.6em;
}

/* The chart follows this bar in the DOM and would otherwise paint over the open dropdown. */
.ridership-time-range-filter ::v-deep .vue__time-picker .dropdown {
  z-index: 1000;
}
</style>
